/**
 * The two admin routes. Both are gated server-side by a constant-time
 * `X-Admin-Token` compare (`tokenMatches` in the Lambda), so the token is the
 * whole boundary — nothing here is a security control, it just carries it.
 *
 * Deliberately reuses `request()` from ./api rather than opening a second fetch
 * wrapper: one place parses error bodies and raises `ApiError`, which is what
 * lets the UI branch on 401 (re-gate) and 404 (already moderated).
 */

import { ApiError, BASE, request } from './api';
import type {
  AdminListResponse,
  HideResponse,
  ModerateResponse,
  StatsResponse,
  SubmissionStatus,
  TrafficStats,
} from '../shared/types';
import { readLogFreshness } from '../shared/logFreshness';

/** API Gateway lowercases header names, and the Lambda checks both spellings. */
const auth = (token: string) => ({ 'X-Admin-Token': token });

/**
 * One page of the queue for a status. The API defaults to `pending` for an
 * unrecognized value, but send it explicitly so the request is self-describing.
 */
export function listQueue(
  status: SubmissionStatus,
  token: string,
  cursor?: string,
): Promise<AdminListResponse> {
  const query = new URLSearchParams({ status });
  if (cursor) query.set('cursor', cursor);
  return request<AdminListResponse>(`/admin/submissions?${query}`, {
    headers: auth(token),
  });
}

/**
 * Approve or reject. Single-shot by design: the Lambda's ConditionExpression
 * only moves an item out of `pending`, so a second call for the same id is a
 * 404 rather than a silent re-stamp. Callers must treat that 404 as "someone
 * already decided this", not as a failure.
 */
export function moderate(
  id: string,
  status: SubmissionStatus,
  token: string,
): Promise<ModerateResponse> {
  return request<ModerateResponse>(`/admin/submissions/${encodeURIComponent(id)}`, {
    method: 'POST',
    body: { status },
    headers: auth(token),
  });
}

/**
 * Take an approved, public post OFF the public wall. ONE-WAY: the API sets
 * public=false and there is no re-publish route. Single-shot like moderate --
 * the Lambda's ConditionExpression only hides an APPROVED item, so a second
 * call (or a non-approved id) is a 404 the caller treats as "already changed",
 * not a failure.
 */
export function hideSubmission(id: string, token: string): Promise<HideResponse> {
  return request<HideResponse>(
    `/admin/submissions/${encodeURIComponent(id)}/hide`,
    { method: 'POST', headers: auth(token) },
  );
}

/**
 * Aggregate submission analytics for the overview. Same token, same 401-re-gate
 * contract as the queue routes. The response is PII-free by construction on the
 * server (see src/functions/api/stats.ts).
 */
export function fetchStats(token: string): Promise<StatsResponse> {
  return request<StatsResponse>('/admin/stats', { headers: auth(token) });
}

/**
 * Download every signer as a CSV Blob. Deliberately NOT via request(): that
 * wrapper parses the body as JSON, and this response is text/csv. A dedicated
 * fetch carries the admin token, re-raises a 401 as ApiError(401) so the UI can
 * re-gate exactly like the other routes, throws on any other non-2xx, and
 * otherwise hands back the raw blob for the browser to save.
 */
export async function exportSigners(token: string): Promise<Blob> {
  const res = await fetch(`${BASE}/admin/export`, { headers: auth(token) });
  if (res.status === 401) {
    throw new ApiError(401, 'Unauthorized');
  }
  if (!res.ok) {
    throw new ApiError(res.status, `Export failed (${res.status})`);
  }
  return res.blob();
}

/**
 * Site-traffic summary. This is NOT an API route -- it is a static JSON file the
 * traffic-agg Lambda writes to the site bucket (stats/traffic.json), fetched
 * same-origin. It is absent until the Phase 2 infra is brought up, and CloudFront
 * rewrites a missing object to index.html with a 200 (see setup.sh
 * CustomErrorResponses), so a 200 that is not JSON of the expected shape must be
 * read as "not available yet" -- never as data. Returns null in that case rather
 * than fabricating anything.
 */
export async function fetchTraffic(): Promise<TrafficStats | null> {
  let res: Response;
  try {
    res = await fetch('/stats/traffic.json', { headers: { accept: 'application/json' } });
  } catch {
    return null;
  }
  if (!res.ok) return null;
  const text = await res.text();
  try {
    const parsed = JSON.parse(text) as Partial<TrafficStats>;
    // Shape check: the SPA-fallback index.html parses as invalid JSON and throws,
    // but guard the fields too so a stray real JSON file cannot masquerade.
    if (
      parsed &&
      typeof parsed.generatedAt === 'string' &&
      Array.isArray(parsed.perDay) &&
      parsed.totals &&
      typeof parsed.totals.pageViews === 'number'
    ) {
      // The freshness fields are optional (older summaries predate them). Keep
      // them only as a well-formed pair; anything malformed (e.g. a numeric
      // newestLogAt) is dropped, which the UI reads as "freshness unknown".
      const { logObjectsInWindow, newestLogAt, ...rest } = parsed;
      const freshness = readLogFreshness({ logObjectsInWindow, newestLogAt });
      return (freshness ? { ...rest, ...freshness } : rest) as TrafficStats;
    }
    return null;
  } catch {
    return null;
  }
}
